import { AfterViewInit, Directive, inject } from '@angular/core';

import { ContentReadyService } from './content-ready.service';

/**
 * A directive that triggers an event when the host component view is initialized.
 *
 * Usage: Add the `vnContentReadyTrigger` attribute to a component that, when rendered, signifies that the page content is ready.
 *
 * Motivation: In navigations, the NavigationEnd event does not signify when the UI is 'ready', visually or interactivity-wise. And using
 * ngAfterViewInit in routed-to components is unreliable because the hooks fire before any dynamically loaded components. Applying this
 * directive to known components that, when painted, signify the UI is ready provides a simple solution that is similar to the custom TTI
 * events raised by some products.
 *
 * For example, you could apply this directive to an event grid in sports, the games section in casino, or the first offer in promo. This creates
 * an event developers can wait on for the UI to be visually ready after navigations before perfoming actions, such as beginning animations or view transitions.
 *
 * Does not wait for images, videos or any dynamic components beneath the one this is applied to.
 */
@Directive({
    selector: '[vnContentReadyTrigger]',
    standalone: true,
})
export class ContentReadyTriggerDirective implements AfterViewInit {
    readonly #contentReadyService = inject(ContentReadyService);

    ngAfterViewInit(): void {
        this.#contentReadyService.trigger();
    }
}
